import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { dailyEntryService } from "../api/services/dailyEntries/dailyEntryService";
import {
  DailyEntry,
  GetDailyEntriesParams,
  CreateDailyEntryRequest,
  UpdateDailyEntryRequest,
} from "../types/dailyEntry";
import { useState, useCallback } from "react";

export const dailyEntryKeys = {
  all: ["dailyEntries"] as const,
  lists: () => [...dailyEntryKeys.all, "list"] as const,
  list: (params: GetDailyEntriesParams) =>
    [...dailyEntryKeys.lists(), params] as const,
  details: () => [...dailyEntryKeys.all, "detail"] as const,
  detail: (id: string) => [...dailyEntryKeys.details(), id] as const,
};

const DAILY_ENTRIES_STALE_TIME =
  Number(process.env.REACT_APP_DAILY_ENTRIES_STALE_TIME) || 300000;

export const useDailyEntries = (params: GetDailyEntriesParams = {}) => {
  return useQuery({
    queryKey: dailyEntryKeys.list(params),
    queryFn: () => dailyEntryService.getDailyEntries(params),
    staleTime: DAILY_ENTRIES_STALE_TIME,
  });
};

export const useDailyEntry = (id: string) => {
  return useQuery({
    queryKey: dailyEntryKeys.detail(id),
    queryFn: () => dailyEntryService.getDailyEntry(id),
    enabled: !!id,
  });
};

export const useCreateDailyEntry = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateDailyEntryRequest) =>
      dailyEntryService.createDailyEntry(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: dailyEntryKeys.lists() });
    },
  });
};

export const useUpdateDailyEntry = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      entryId,
      data,
    }: {
      entryId: string;
      data: UpdateDailyEntryRequest;
    }) => dailyEntryService.updateDailyEntry(entryId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: dailyEntryKeys.lists() });
    },
  });
};

export const useDeleteDailyEntry = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (entryId: string) =>
      dailyEntryService.deleteDailyEntry(entryId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: dailyEntryKeys.lists() });
    },
  });
};

export const useCreateCustomerEntry = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (customerId: string) =>
      dailyEntryService.createCustomerEntry(customerId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: dailyEntryKeys.lists() });
    },
  });
};

export const useDailyEntriesForExport = (
  params: GetDailyEntriesParams = {},
) => {
  const exportParams = { ...params, export: true };

  return useQuery({
    queryKey: [...dailyEntryKeys.list(exportParams), "export"],
    queryFn: () => dailyEntryService.getDailyEntries(exportParams),
    enabled: false,
    staleTime: 0,
  });
};

export const useDailyEntryManagement = (
  initialParams: GetDailyEntriesParams = {},
) => {
  const [error, setError] = useState<Error | null>(null);

  const {
    data: dailyEntriesData,
    isLoading,
    isError,
    error: queryError,
    refetch,
  } = useDailyEntries(initialParams);

  const {
    isLoading: isExporting,
    error: exportError,
    refetch: refetchExport,
  } = useDailyEntriesForExport(initialParams);

  const createDailyEntryMutation = useCreateDailyEntry();
  const updateDailyEntryMutation = useUpdateDailyEntry();
  const deleteDailyEntryMutation = useDeleteDailyEntry();
  const createCustomerEntryMutation = useCreateCustomerEntry();

  const refreshDailyEntries = useCallback(() => {
    refetch();
  }, [refetch]);

  const createDailyEntry = useCallback(
    (
      data: CreateDailyEntryRequest,
      options?: {
        onSuccess?: (dailyEntry: DailyEntry) => void;
        onError?: (error: Error) => void;
      },
    ) => {
      return createDailyEntryMutation.mutate(data, {
        onSuccess: (dailyEntry) => {
          setError(null);
          options?.onSuccess?.(dailyEntry);
        },
        onError: (error) => {
          setError(error as Error);
          options?.onError?.(error as Error);
        },
      });
    },
    [createDailyEntryMutation],
  );

  const updateDailyEntry = useCallback(
    (
      entryId: string,
      data: UpdateDailyEntryRequest,
      options?: {
        onSuccess?: (dailyEntry: DailyEntry) => void;
        onError?: (error: Error) => void;
      },
    ) => {
      return updateDailyEntryMutation.mutate(
        { entryId, data },
        {
          onSuccess: (dailyEntry) => {
            setError(null);
            options?.onSuccess?.(dailyEntry);
          },
          onError: (error) => {
            setError(error as Error);
            options?.onError?.(error as Error);
          },
        },
      );
    },
    [updateDailyEntryMutation],
  );

  const deleteDailyEntry = useCallback(
    (
      entryId: string,
      options?: {
        onSuccess?: () => void;
        onError?: (error: Error) => void;
      },
    ) => {
      return deleteDailyEntryMutation.mutate(entryId, {
        onSuccess: () => {
          setError(null);
          options?.onSuccess?.();
        },
        onError: (error) => {
          setError(error as Error);
          options?.onError?.(error as Error);
        },
      });
    },
    [deleteDailyEntryMutation],
  );

  const createCustomerEntry = useCallback(
    (
      customerId: string,
      options?: {
        onSuccess?: (dailyEntry: DailyEntry) => void;
        onError?: (error: Error) => void;
      },
    ) => {
      return createCustomerEntryMutation.mutate(customerId, {
        onSuccess: (dailyEntry) => {
          setError(null);
          options?.onSuccess?.(dailyEntry);
        },
        onError: (error) => {
          setError(error as Error);
          options?.onError?.(error as Error);
        },
      });
    },
    [createCustomerEntryMutation],
  );

  const exportDailyEntries = useCallback(async () => {
    try {
      const result = await refetchExport();
      return result.data;
    } catch (error) {
      setError(error as Error);
      throw error;
    }
  }, [refetchExport]);

  return {
    dailyEntries: dailyEntriesData?.dailyEntries || [],
    total: dailyEntriesData?.total || 0,
    skip: dailyEntriesData?.skip || 0,
    limit: dailyEntriesData?.limit || 100,
    isLoading,
    isError,
    error: queryError || error,
    refreshDailyEntries,

    exportDailyEntries,
    isExporting,
    exportError,

    createDailyEntry,
    updateDailyEntry,
    deleteDailyEntry,
    createCustomerEntry,

    isCreating: createDailyEntryMutation.isPending,
    isUpdating: updateDailyEntryMutation.isPending,
    isDeleting: deleteDailyEntryMutation.isPending,
    isCreatingCustomerEntry: createCustomerEntryMutation.isPending,

    createError: createDailyEntryMutation.error as Error | null,
    updateError: updateDailyEntryMutation.error as Error | null,
    deleteError: deleteDailyEntryMutation.error as Error | null,
    createCustomerEntryError: createCustomerEntryMutation.error as Error | null,
  };
};
